import type { IconButtonProps } from '@mui/material/IconButton';
import type { 
  NotificationItem,
  MediaProcessingUpdate
} from 'src/types/event-source';

import { toast } from 'sonner';
import { useState, useEffect, useCallback } from 'react';

import { Error, Schedule, CheckCircle } from '@mui/icons-material';
import { 
  Box, 
  Chip, 
  List, 
  Drawer, 
  Button,
  Divider,
  Tooltip,
  ListItem,
  IconButton,
  Typography,
  ListItemIcon
} from '@mui/material';

import { getImageThumbnail } from 'src/utils/file-service';

import { useDeleteImage } from 'src/actions/image';
import { useMediaUpdates } from 'src/contexts/media-updates-context';

import { Image } from 'src/components/image';
import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';




// ----------------------------------------------------------------------

export type NotificationDrawerProps = IconButtonProps;

export function NotificationDrawer({ sx, ...other }: NotificationDrawerProps) {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [deletingGuid, setDeletingGuid] = useState<string | null>(null);

  const { isConnected, isConnecting, subscribe, subscribeToError } = useMediaUpdates();
  const { deleteImage, isDeleting } = useDeleteImage();

  useEffect(() => {
    subscribe((update) => {
      const notification = createMediaUpdateNotification(update);
        const filteredPrev = update.guid 
          ? notifications.filter(n => n.guid !== update.guid)
          : notifications;
      setNotifications(prev => [notification, ...filteredPrev]);
    });
  }, [subscribe, notifications]);

  useEffect(() => {
    subscribeToError((error) => {
      const notification = createErrorNotification(error);
      setNotifications(prev => [notification, ...prev]);
    });
  }, [subscribeToError, notifications]);

  // useEffect(() => {
  //   subscribeToHeartbeat((heartbeat) => {
  //     const notification = createHeartbeatNotification(heartbeat);
  //     const filteredPrev = heartbeat.occurredAt 
  //       ? notifications.filter(n => n.occurredAt !== heartbeat.occurredAt)
  //       : notifications;
  //     setNotifications(prev => [notification, ...filteredPrev]);
  //   });
  // }, [subscribeToHeartbeat]);

  const handleOpenDrawer = useCallback(() => {
    setOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setOpen(false);
  }, []);

  const handleClearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const handleDeleteImage = useCallback(async (guid: string, notificationId: string) => {
    if (!guid) return;
    
    setDeletingGuid(guid);
    try {
      await deleteImage(guid);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      toast.success('Image deleted successfully', { duration: 5000 });
    } catch (error) {
      // Error is handled by the hook, but we can keep the notification visible
      console.error('Failed to delete image:', error);
    } finally {
      setDeletingGuid(null);
    }
  }, [deleteImage]);

  const getStateIcon = (state: string) => {
    switch (state) {
      case 'completed':
        return <CheckCircle color="success" fontSize="small" />;
      case 'failed':
        return <Error color="error" fontSize="small" />;
      case 'processing':
        return <Schedule color="warning" fontSize="small" />;
      default:
        return null;
    }
  };

  const getStateColor = (state: string) => {
    switch (state) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'processing':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatTimestamp = (timestamp: string) => new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const createErrorNotification = (error: Error): NotificationItem => ({
    id: `error-${Date.now()}`,
    type: 'error',
    state: 'error',
    title: 'Media Processing Error',
    message: 'An error occurred with the media processing connection',
    occurredAt: new Date().toISOString(),
    error: error.message,
  });

  // Helper functions to convert different event types to unified NotificationItem
  const createMediaUpdateNotification = (update: MediaProcessingUpdate): NotificationItem => {
    const getStateMessage = (state: string, type?: string, placeName?: string) => {
      const baseMessage = (() => {
        switch (state) {
          case 'processing':
            return `${type || 'Media'} is being processed`;
          case 'completed':
            return `${type || 'Media'} processing completed successfully`;
          case 'failed':
            return `${type || 'Media'} processing failed`;
          default:
            return `${type || 'Media'} processing ${state}`;
        }
      })();

      // If this is a place-related notification, include the place name
      if (placeName) {
        return `${baseMessage} for ${placeName}`;
      }
      return baseMessage;
    };

    const getTitle = (name?: string, type?: string, state?: string) => {
      if (name) return name;
      if (type && state) return `${type.charAt(0).toUpperCase() + type.slice(1)} Processing`;
      return 'Media Processing Update';
    };

    // Check if this is a place-related notification by looking at the name
    // If the name looks like a place name (not a file name), treat it as a place
    const isPlaceNotification = update.name && !update.name.includes('.') && update.name.length > 0;
    const placeName = isPlaceNotification ? update.name : undefined;

    return {
      id: `${update.guid}-${update.occurredAt}`,
      type: 'media_update',
      state: update.state,
      title: getTitle(update.name, update.type, update.state),
      message: getStateMessage(update.state, update.type, placeName),
      occurredAt: update.occurredAt,
      error: update.error ? String(update.error) : undefined,
      guid: update.guid,
      s3Guid: update.s3Guid,
      mediaType: update.type,
    };
  };

  // const createHeartbeatNotification = (heartbeat: SSEHeartbeatEvent): NotificationItem => {
  //   return {
  //     id: `heartbeat-${Date.now()}`,
  //     type: 'heartbeat',
  //     state: 'heartbeat',
  //     title: 'Heartbeat',
  //     message: 'Heartbeat received',
  //     occurredAt: heartbeat.occurredAt,
  //   };
  // };



  return (
    <>
      <IconButton
        onClick={handleOpenDrawer}
        sx={{
          position: 'relative',
          ...sx
        }}
        {...other}
      >
        <Iconify icon="mdi:bell" />
        {notifications.length >= 1 && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: 'error.main',
              border: '2px solid',
              borderColor: 'background.paper'
            }}
          />
        )}
      </IconButton>

      <Drawer
        open={open}
        onClose={handleCloseDrawer}
        anchor="right"
        slotProps={{ backdrop: { invisible: true } }}
        PaperProps={{ sx: { width: 320 } }}
      >
        <IconButton
          onClick={handleCloseDrawer}
          sx={{ top: 12, left: 12, zIndex: 9, position: 'absolute' }}
        >
          <Iconify icon="mingcute:close-line" />
        </IconButton>

        <Box sx={{ top: 12, right: 12, zIndex: 9, position: 'absolute', display: 'flex', gap: 1, alignItems: 'center', p: 1 }}>
              {notifications.length > 0 && (
                <IconButton
                  onClick={handleClearAllNotifications}
                  size="small"
                  sx={{ 
                    color: 'text.secondary',
                    '&:hover': {
                      color: 'text.primary',
                      backgroundColor: 'action.hover'
                    }
                  }}
                  title="Clear all notifications"
                >
                  <Iconify icon="mdi:delete-sweep" />
                </IconButton>
              )}
              <Chip
                label={isConnected ? 'Connected' : isConnecting ? 'Connecting' : 'Disconnected'}
                color={isConnected ? 'success' : isConnecting ? 'warning' : 'default'}
                size="small"
              />
            </Box>

        <Scrollbar sx={{ p: 2, mt: 4 }}>
          <Box sx={{ mb: 2 }}>

            {/* {mediaError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Error: {mediaError.message}
              </Alert>
            )} */}
          </Box>

          <Divider sx={{ mb: 2 }} />

          {notifications.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                No notifications yet
              </Typography>
            </Box>
          ) : (
            <List>
              {notifications.map((notification) => (
                <ListItem key={notification.id} sx={{ px: 0, mb: 1, flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', mb: 1, gap: 1 }}>
                    {/* Thumbnail for image notifications */}
                    {notification.mediaType === 'image' && notification.s3Guid && (
                      <Box sx={{ 
                        width: 48, 
                        height: 48, 
                        borderRadius: 1, 
                        overflow: 'hidden',
                        flexShrink: 0,
                        border: '1px solid',
                        borderColor: 'divider',
                        backgroundColor: 'background.neutral'
                      }}>
                        <Image
                          alt={notification.title}
                          src={getImageThumbnail(notification.s3Guid)}
                          sx={{ 
                            width: '100%', 
                            height: '100%',
                            objectFit: 'cover'
                          }}
                          onError={() => {
                            // Fallback to icon if image fails to load
                          }}
                        />
                      </Box>
                    )}
                    
                    {/* State icon for non-image notifications or as fallback */}
                    {notification.mediaType !== 'image' && (
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {getStateIcon(notification.state)}
                      </ListItemIcon>
                    )}
                    
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Chip 
                          label={notification.state} 
                          color={getStateColor(notification.state) as any}
                          size="small" 
                        />
                        {notification.mediaType && (
                          <Chip 
                            label={notification.mediaType} 
                            variant="outlined"
                            size="small" 
                          />
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                          {formatTimestamp(notification.occurredAt)}
                        </Typography>
                      </Box>
                      {notification.message && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          {notification.message}
                        </Typography>
                      )}
                      {notification.error && (
                        <Typography variant="body2" color="error.main" sx={{ mb: 0.5 }}>
                          {notification.error}
                        </Typography>
                      )}
                                        {/* Delete button for failed image notifications */}
                  {notification.state === 'failed' && 
                   notification.mediaType === 'image' && 
                   notification.guid && (
                    <Box sx={{ width: '100%', mt: 1 }}>
                      <Tooltip title="Delete this failed image">
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<Iconify icon="mdi:delete" />}
                          onClick={() => handleDeleteImage(notification.guid!, notification.id)}
                          disabled={isDeleting && deletingGuid === notification.guid}
                        >
                          {isDeleting && deletingGuid === notification.guid ? 'Deleting...' : 'Delete Image'}
                        </Button>
                      </Tooltip>
                    </Box>
                  )}
                    </Box>
                  </Box>
                </ListItem>
              ))}
            </List>
          )}
        </Scrollbar>
      </Drawer>
    </>
  );
}
